import { Controller } from "@hotwired/stimulus"

// The collapsed rail's hover behaviour (shared/_sidebar, shared/menu/_strip_group,
// shared/menu/_flyout). Everything it shows grows out of the rail's right edge:
//
// - resting on a page glyph ([data-rail-tip]) grows a tongue with the page's name
//   and count; the tongue is itself the link, stays while the mouse crosses onto
//   it, and goes once the mouse has left both
// - resting on a group glyph ([data-rail-fly]) fans out that group's flyout
//   ([data-rail-fly-panel]); click toggles it too, for touch and keyboards
//
// Both wait a beat before appearing so a mouse crossing the rail doesn't flicker
// through every glyph, and both linger a beat after the mouse leaves so the
// mouse can reach them. Only active while the strip is showing (desktop,
// railCollapsed); the expanded rail and the mobile drawer never see any of it.
const INTENT_MS = 70
const GRACE_MS = 160
const TONGUE_HEIGHT = 36

export default class extends Controller {
  static targets = ["strip", "layer", "tongue", "tongueLabel", "tongueCount", "tongueKbd"]

  connect() {
    this.onOver = this.onOver.bind(this)
    this.onOut = this.onOut.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onKey = this.onKey.bind(this)
    this.closeAll = this.closeAll.bind(this)
    this.keep = this.keep.bind(this)
    this.release = this.release.bind(this)
    this.onTongueClick = this.onTongueClick.bind(this)

    this.element.addEventListener("mouseover", this.onOver)
    this.element.addEventListener("mouseout", this.onOut)
    this.element.addEventListener("click", this.onClick)
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKey)
    document.addEventListener("turbo:frame-load", this.closeAll)
    window.addEventListener("resize", this.closeAll)

    if (this.hasLayerTarget) {
      this.layerTarget.addEventListener("mouseenter", this.keep)
      this.layerTarget.addEventListener("mouseleave", this.release)
    }
    if (this.hasTongueTarget) this.tongueTarget.addEventListener("click", this.onTongueClick)
  }

  disconnect() {
    this.closeAll()
    this.element.removeEventListener("mouseover", this.onOver)
    this.element.removeEventListener("mouseout", this.onOut)
    this.element.removeEventListener("click", this.onClick)
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKey)
    document.removeEventListener("turbo:frame-load", this.closeAll)
    window.removeEventListener("resize", this.closeAll)
    if (this.hasLayerTarget) {
      this.layerTarget.removeEventListener("mouseenter", this.keep)
      this.layerTarget.removeEventListener("mouseleave", this.release)
    }
    if (this.hasTongueTarget) this.tongueTarget.removeEventListener("click", this.onTongueClick)
  }

  // The strip is only laid out when the rail is collapsed on desktop
  get collapsed() {
    return this.hasStripTarget && this.stripTarget.offsetParent !== null
  }

  // ---- hover ----

  onOver(event) {
    if (!this.collapsed) return
    const source = event.target.closest("[data-rail-tip], [data-rail-fly]")
    if (!source || !this.element.contains(source)) return
    if (source.contains(event.relatedTarget)) return

    clearTimeout(this.intentTimer)
    if (source.dataset.railFly !== undefined) {
      clearTimeout(this.closeTimer)
      this.intentTimer = setTimeout(() => this.openFly(source), INTENT_MS)
    } else {
      if (this.fly) this.scheduleClose()
      this.showTongue(source)
    }
  }

  onOut(event) {
    const source = event.target.closest("[data-rail-tip], [data-rail-fly]")
    if (!source || source.contains(event.relatedTarget)) return
    clearTimeout(this.intentTimer)
    if (source === this.tongueFor) this.scheduleHideTongue()
    if (this.fly) this.scheduleClose()
  }

  // The mouse has reached the tongue or a flyout: hold them
  keep() {
    clearTimeout(this.closeTimer)
    clearTimeout(this.tongueTimer)
  }

  release() {
    this.scheduleHideTongue()
    if (this.fly) this.scheduleClose()
  }

  // ---- tongue ----

  showTongue(source) {
    if (source === this.tongueFor) {
      clearTimeout(this.tongueTimer)
      return
    }
    this.hideTongue()
    this.tongueFor = source
    this.tongueLabelTarget.textContent = source.dataset.railTip
    this.tongueCountTarget.textContent = this.countFor(source)
    this.tongueKbdTarget.textContent = source.dataset.railKbd || ""
    const href = source.getAttribute("href")
    this.tongueTarget.setAttribute("href", href || "#")
    this.tongueTarget.hidden = false

    const rail = this.element.getBoundingClientRect()
    const r = source.getBoundingClientRect()
    this.tongueTarget.style.top = `${r.top - rail.top + (r.height - TONGUE_HEIGHT) / 2}px`
  }

  // A count reads from the expanded rail's own badge, so it is the live figure
  countFor(source) {
    const id = source.dataset.railBadge
    if (!id) return ""
    const badge = this.element.querySelector(`.menu-item [data-menu-badge="${id}"]`)
    if (!badge || badge.classList.contains("hidden")) return ""
    return badge.textContent.trim()
  }

  hideTongue() {
    clearTimeout(this.tongueTimer)
    if (!this.hasTongueTarget) return
    this.tongueTarget.hidden = true
    this.tongueFor = null
  }

  scheduleHideTongue() {
    clearTimeout(this.tongueTimer)
    this.tongueTimer = setTimeout(() => this.hideTongue(), GRACE_MS)
  }

  // The tongue stands for its glyph: a link follows the link, a button presses
  // the button (the expand mark, the user chip)
  onTongueClick(event) {
    const source = this.tongueFor
    if (source && !source.getAttribute("href")) {
      event.preventDefault()
      source.click()
    }
    this.hideTongue()
  }

  // ---- flyouts ----

  openFly(source) {
    const key = source.dataset.railFly
    if (this.flyKey === key) return
    this.closeFly()
    this.hideTongue()

    const panel = this.layerTarget.querySelector(`[data-rail-fly-panel="${key}"]`)
    if (!panel) return
    panel.hidden = false
    source.classList.add("rail-glyph-hover")
    this.fly = panel
    this.flyFor = source
    this.flyKey = key

    const rail = this.element.getBoundingClientRect()
    const r = source.getBoundingClientRect()
    const height = panel.offsetHeight
    let top = r.top - rail.top - 8
    top = Math.max(8, Math.min(top, rail.height - height - 8))
    panel.style.top = `${top}px`
  }

  closeFly() {
    clearTimeout(this.closeTimer)
    if (this.fly) this.fly.hidden = true
    if (this.flyFor) this.flyFor.classList.remove("rail-glyph-hover")
    this.fly = null
    this.flyFor = null
    this.flyKey = null
  }

  scheduleClose() {
    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(() => this.closeFly(), GRACE_MS)
  }

  closeAll() {
    clearTimeout(this.intentTimer)
    this.hideTongue()
    this.closeFly()
  }

  // ---- clicks and keys ----

  onClick(event) {
    if (event.target.closest("[data-rail-toggle]")) {
      this.closeAll()
      return
    }
    // A glyph pressed directly: its tongue has done its job
    if (event.target.closest("[data-rail-tip]")) this.hideTongue()
    const group = event.target.closest("[data-rail-fly]")
    if (group && this.collapsed) {
      event.preventDefault()
      if (this.flyKey === group.dataset.railFly) this.closeFly()
      else this.openFly(group)
      return
    }
    // A page chosen from a flyout: Turbo takes the link, the flyout goes
    if (event.target.closest(".rail-fly a[href]")) this.closeAll()
  }

  onDocumentClick(event) {
    if (!this.element.contains(event.target)) this.closeAll()
  }

  onKey(event) {
    if (event.key === "Escape" && (this.fly || this.tongueFor)) this.closeAll()
  }
}
